import React, { useEffect, useState } from 'react';
import { Button, Container, IconButton, Typography, Box, Tooltip, TextField, Stack, FormControl, InputLabel, Select, MenuItem,   Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Checkbox
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import '../../Calendar.css'; 
import MiniDrawer from '../../components/MiniDrawer.js';
import { useTheme } from '@mui/material/styles';
import useAlert from '../../alert/useAlert.js';
import AlertDialog from '../../Dialog.js';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UserView from './UserView.js';
import PopupMenu from '../PopupMenu/PopupMenu.js';
import { ScribeButton } from '../../components/scribe/ScribeUI';
import { supabase } from '../../supabaseClient.js';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { setRole } from '../../store.js';
import Loader from '../../loader.js';
import axios from 'axios';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { addUserToTeamFlow } from './userInvitationFlow.js';

const UserList = ({ embedded = false }) => {
  const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });
  const theme = useTheme();
  const { setAlert } = useAlert();
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);

  const dispatch = useDispatch();

  const [view, setView] = useState("allUsers");
  const [selectedUser, setSelectedUser] = useState(null);
  const [addUser, setAddUser] = useState(false);
  const [addCollaborators, setAddCollaborators] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [originalCollaborators, setOriginalCollaborators] = useState([]);
  const [userInvited, setUserInvited] = useState(false);
  const [email, setEmail] = useState('');
  const [userRole, setUserRole] = useState('');
  const [teamName, setTeamName] = useState('');
  const [allTeamNames, setAllTeamNames] = useState([]);
  const [allTeamIDs, setAllTeamIDs] = useState([]);
  const [adminTeamNames, setAdminTeamNames] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [teamMembersData, setTeamMembersData] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState(null);

  const navigate = useNavigate();

  // const roles = ['Admin', 'Member', 'Guest'];
  const roles = ['Admin', 'Member'];

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchUsers();
    fetchAdminTeams();
    getTeamMembers();
    checkIfAdmin();
    getCollaborators();
  }, [currentOrg]);

  const fetchSubscriptionInfo = async () => {
    try {
      if (!userDetails || !userDetails.id) return;
      
      const { data, error } = await supabase.from('subscription_info').select('is_active').eq('user_id', userDetails.id).single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription info:', error);
        return;
      }
      if(!data) {
        setSubscriptionActiveStatus(false);
        return;
      }
      if (data) {
        setSubscriptionActiveStatus(data.is_active);
      }
    } catch (error) {
      console.error('Error in fetchSubscriptionInfo:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    const { data: teams } = await supabase.from('team_users').select('team_id').eq('user_id', userDetails.id);
    const teamIDs = teams.map(team => team.team_id);
    const teamNames = await getTeamNames(teamIDs);
    setAllTeamIDs(teamIDs);
    setAllTeamNames(teamNames);

    const currentOrgIndex = teamNames.indexOf(currentOrg ? currentOrg.name : '');
    if (currentOrgIndex !== -1) {
      const filteredTeamID = teamIDs[currentOrgIndex];
      const filteredTeamName = teamNames[currentOrgIndex];

      const { data, error } = await supabase.from('team_users').select('*').eq('team_id', filteredTeamID);
      if (error) {
        console.error("Error: ", error);
        setLoading(false);
        return;
      }
      
      const users = [];
      for (let teamUser of data) {
        const userData = await getUserDetails(teamUser.user_id);
        users.push(
          {
            name: userData.name,
            email: userData.email,
            teamName: filteredTeamName,
            role: teamUser.role,
            user_id: teamUser.user_id,
            team_id: teamUser.team_id
          }
        );
      }
      setUserList(users);
      setLoading(false);
    } else {
      setUserList([]);
      setLoading(false);
    }
  }

  const fetchAdminTeams = async () => {
    const { data: adminTeams } = await supabase.from('team_users').select('team_id').eq('user_id', userDetails.id).eq('role', 'Admin');
    const adminTeamIDs = adminTeams.map(team => team.team_id);
    const adminTeamNames = await getTeamNames(adminTeamIDs);
    setAdminTeamNames(adminTeamNames);
  }

  const checkIfAdmin = async () => {
    const teamData = await getTeamID(currentOrg ? currentOrg.name : '');
    if(!teamData) {
      return;
    }
    const { data, error } = await supabase.from('team_users').select('role').eq('user_id', userDetails.id).eq('team_id', teamData.id);
    if (error) {
      console.error("Error: ", error);
      return;
    }
    if(data.length > 0 && data[0].role === 'Admin') {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
    dispatch(setRole(data[0].role));
  }

  const getTeamID = async (teamName) => {
    const { data, error } = await supabase.from('teams').select('*');
    if (error) {
      console.error('Error getting team ID: ', error.message);
      return null;
    } else {
      const decryptedData = await Promise.all(data.map(async (team) => {
        const decryptedTeamName = await supabase.rpc('decrypt_text', { encrypted_data: team.name });
        if (decryptedTeamName.error) {
          console.error("Error: ", decryptedTeamName.error);
          return null;
        }
        return { ...team, name: decryptedTeamName.data };
      }));
      return decryptedData.find(team => team.name === teamName);
    }
  }

  const getUserByEmail = async(email) => {
    const { data, error } = await supabase.from('user_accounts').select('*');
    if (error) {
      throw error;
    } else {
      const decryptedData = await Promise.all(data.map(async (user) => {
        const decryptedEmail = await supabase.rpc('decrypt_text', { encrypted_data: user.email });
        if (decryptedEmail.error) {
          throw decryptedEmail.error;
        }
        return { ...user, email: decryptedEmail.data };
      }));
      return decryptedData.find(user => user.email?.trim().toLowerCase() === email.trim().toLowerCase()) || null;
    }
  }

  const getUserDetails = async (id) => {
    let { data, error } = await supabase.from('user_accounts').select('*').eq('id', id);
    if (error) {
      console.error("Error: ", error);
      return null;
    } else {
      if(data.length > 0) {
        const [name, email] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: data[0].name }),
          supabase.rpc('decrypt_text', { encrypted_data: data[0].email }),
        ]);
        if(name.error || email.error) {
          setAlert('Error decrypting user details', 'error');
          return null;
        }
        return { name: name.data, email: email.data };
      }
      return null;
    }
  }

  const getCollaborators = async () => {
    if(!currentOrg) {
      return;
    }
    const { data, error } = await supabase.from('teams').select('collaborators').eq('id', currentOrg.id).single();
    if (error) {
      console.error("Error: ", error);
      return;
    }
    if(data && data.collaborators) {
      setCollaborators(data.collaborators);
      setOriginalCollaborators(data.collaborators);
    }

  }

  const handleSelectedUser = (user) => {
    setView("userView");
    setSelectedUser(user);
  }

  const handleAddCollaborators = () => {
    setFormSubmitted(false);
    setAddCollaborators(true);
    setEmail('');
    setUserRole('');
    setTeamName('');
  }

  const handleCloseCollaborators = () => {
    setCollaborators([...originalCollaborators]);
    setAddCollaborators(false);
  }

  const handleAddUser = () => {
    setUserInvited(false);
    setAddUser(true);
    setEmail('');
    setUserRole('');
    setTeamName('');
  }

  const handleRemoveUser = async (user) => {
    const { error } = await supabase.from('team_users').delete().eq('user_id', user.user_id).eq('team_id', user.team_id);
    if (error) {
      setAlert(error.message, 'error');
      return;
    }

    const { error: doctorDeleteError } = await supabase
      .from('doctors')
      .delete()
      .eq('team_id', user.team_id)
      .eq('user_id', user.user_id);

    if (doctorDeleteError) {
      console.error("Error deleting doctor entry:", doctorDeleteError);
    }
    fetchUsers();
    setAlert('User removed from the team successfully!', 'success');
  };

  const inviteUser = async () => {
    setAddUser(false);
    setLoading(true);
    try {
      const teamId = allTeamIDs[allTeamNames.indexOf(teamName)];
      const result = await addUserToTeamFlow({
        email,
        userRole,
        teamName,
        teamId,
        redirectUrl: process.env.REACT_APP_USER_INVITE_LINK,
        findScribeUser: () => getUserByEmail(email),
        findTeamMembership: async (userId) => {
          const { data, error } = await supabase.from('team_users').select('*')
            .eq('user_id', userId).eq('team_id', teamId).maybeSingle();
          if (error) throw error;
          return data;
        },
        addScribeUserToTeam: async (userData) => {
          const teamData = await getTeamID(teamName);
          if (!teamData) throw new Error('Could not find team data to complete the operation');
          const { error } = await supabase.from('team_users').insert([{
            team_id: teamData.id,
            user_id: userData.id,
            role: userRole,
          }]);
          if (error) throw error;

          const { data: doctorOrderData, error: doctorOrderError } = await supabase
            .from('doctors').select('display_order').eq('team_id', teamData.id)
            .order('display_order', { ascending: false }).limit(1);
          if (doctorOrderError) throw doctorOrderError;
          const nextDisplayOrder = doctorOrderData?.[0]?.display_order
            ? doctorOrderData[0].display_order + 1 : 1;
          const { error: doctorInsertError } = await supabase.from('doctors').insert([{
            team_id: teamData.id,
            user_id: userData.id,
            display_order: nextDisplayOrder,
          }]);
          if (doctorInsertError) throw doctorInsertError;
        },
        inviteNewUser: async (inviteDetails) => {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) throw sessionError;
          const accessToken = sessionData?.session?.access_token;
          if (!accessToken) throw new Error('Your session has expired. Please sign in again.');
          return axios.post(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/inviteUserByEmail`,
            inviteDetails,
            { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' } }
          );
        },
      });

      if (result === 'already_in_team') {
        setAlert('User already exists in the organization. Please try using a different email', 'error');
        return;
      }
      if (result === 'added_to_team') {
        setAlert('User added to the team successfully!', 'success');
      } else {
        setAlert('User invited successfully to create an account and join the team!', 'success');
      }
      fetchUsers();
    } catch (err) {
      const errorResponse = err.response?.data;
      if (errorResponse?.code === 'auth_user_without_scribe_profile') {
        setAlert('An account with this email already exists but is not set up for Sophie Assist. The account was not changed. Please contact support.', 'error');
      } else if (['auth_lookup_failed', 'auth_lookup_incomplete', 'auth_lookup_ambiguous'].includes(errorResponse?.code)) {
        setAlert('Unable to verify this email safely. Please try again later.', 'error');
      } else {
        setAlert(errorResponse?.details || 'Failed to add user. Please try again later.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }

  const addCollaboratorsToTeam = async () => {
    const { error } = await supabase.from('teams').update({ collaborators: collaborators }).eq('id', currentOrg.id);
    if (error) {
      setAlert(error.message, 'error');
    }
    else {
      setAddCollaborators(false);
      setAlert('Collaborators updated successfully!', 'success');
      getCollaborators();
    }
  }

  const getTeamNames = async (teamIDs) => {
    try {
      const teamNamePromises = teamIDs.map(async (team_id) => {
        const { data, error } = await supabase.from('teams').select('name').eq('id', team_id);
        if (error) {
          throw new Error(`Error fetching team name for team_id ${team_id}: ${error.message}`);
        }
        const decryptedTeamName = await supabase.rpc('decrypt_text', { encrypted_data: data[0].name });
        if (decryptedTeamName.error) {
          throw new Error(`Error decrypting team name for team_id ${team_id}: ${decryptedTeamName.error.message}`);
        }
        return decryptedTeamName.data;
      });

      const teamNames = await Promise.all(teamNamePromises);
      return teamNames;
    } catch (error) {
      console.error("Error: ", error);
      return [];
    }
  }

  const getTeamMembers = async () => {
    if(!currentOrg) {
      return;
    }
    try {
      const { data: memberData, error: memberDataError } = await supabase
        .from('team_users')
        .select('user_id')
        .eq('team_id', currentOrg ? currentOrg.id : '');
        
      if (memberDataError) {
        console.error("Error fetching team members:", memberDataError);
        return;
      }
      
      const teamMembersIDs = memberData.map(member => member.user_id);
      const { data: usersData, error: usersError } = await supabase
        .from('user_accounts')
        .select('id, name')
        .in('id', teamMembersIDs);
        
      if (usersError) {
        console.error("Error fetching user accounts:", usersError);
        return;
      }
      
      const decryptedUsers = await Promise.all(
        usersData.map(async (userData) => {
          const { data: decryptedName, error: decryptError } = await supabase.rpc(
            'decrypt_text', 
            { encrypted_data: userData.name }
          );
          
          if (decryptError) {
            console.error("Error decrypting name:", decryptError);
            return "Error decrypting name";
          }
          return {
            id: userData.id,
            name: decryptedName
          }
        })
      );
      setTeamMembersData(decryptedUsers);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };
  
  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{overflow: 'auto', ...(embedded ? {} : { minHeight: '100vh', minWidth: '100vw' })}}>
        {!embedded && (
          <Grid size={{xs: 1, md: 0}}>
            <MiniDrawer />
          </Grid>
        )}
        <Grid size="grow">
          <Grid container spacing={5} sx={{ backgroundColor: embedded ? 'transparent' : '#F8F9FA', display: 'flex', flexDirection: 'column',  width: '100%', height: '100%' }}>
            <Grid size={12}>
              <div style={{ padding: embedded ? '8px 0' : '24px 10vw' }}>
                <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                    {view === "allUsers" && (
                        <>
                        <Grid
                            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
                        >
                            <Typography variant="h4" sx={{marginRight: '20px'}}>Users</Typography>
                            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                              {/* {
                                isAdmin && (
                                  <Button onClick={() => { handleAddCollaborators(); }} variant="outlined" startIi={<AddIcon sx={{color: '#fff'}}/>} sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                                    <Typography variant="button" sx={{color: '#FFFFFF'}}>Add Collaborators</Typography>
                                  </Button>
                                )
                              } */}
                              {
                                isAdmin && (
                                  <Button onClick={() => { handleAddUser() }} variant="outlined" startIcon={<AddIcon sx={{color: '#fff'}}/>} sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                                    <Typography variant="button" sx={{color: '#FFFFFF'}}>Add User</Typography>
                                  </Button>
                                )
                              }
                            </Grid>
                        </Grid>
                        { loading ?
                            (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                <Loader />
                              </Box>
                            )
                        :
                        <TableContainer component={Paper} sx={{ mb: 4, borderRadius: '16px', }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell align="left"><Typography>Name</Typography></TableCell>
                                <TableCell align="left"><Typography>Email</Typography></TableCell>
                                <TableCell align="left"><Typography>Organization</Typography></TableCell>
                                <TableCell align="left"><Typography>Role</Typography></TableCell>
                                {
                                  isAdmin && (
                                    <TableCell align="left"><Typography>Action</Typography></TableCell>
                                  )
                                }
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {
                                userList.map((user, index) => (
                                  <TableRow key={index}>
                                    <TableCell align="left">{user.name}</TableCell>
                                    <TableCell align="left">{user.email}</TableCell>
                                    <TableCell align="left">{user.teamName}</TableCell>
                                    <TableCell align="left">{user.role}</TableCell>
                                    {
                                      isAdmin && (
                                        <TableCell align="left">
                                          <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                                            <Tooltip title="Edit Details">
                                              <IconButton 
                                                onClick={(e) => { e.stopPropagation(); handleSelectedUser(user); }} 
                                                sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px', width: '40px', height: '40px' }}
                                              >
                                                <EditIcon />
                                              </IconButton>
                                            </Tooltip>

                                            {
                                              user.user_id !== userDetails.id && (
                                                <Tooltip title="Remove User">
                                                  <IconButton 
                                                    onClick={(e) => { e.stopPropagation(); setOpenDialog({open: true, content: "Are you sure you want to remove this user?", confirmAction: () => handleRemoveUser(user)}); }} 
                                                    sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px', width: '40px', height: '40px' }}
                                                  >
                                                    <DeleteIcon />
                                                  </IconButton>
                                                </Tooltip>
                                              )
                                            }
                                          </Stack>
                                        </TableCell>
                                      )
                                    }
                                  </TableRow>
                                ))
                              }
                            </TableBody>
                          </Table>
                        </TableContainer>
                        }
                        </>
                    )}
                    {view === "userView" && (
                      <UserView
                        onBack={() => { fetchUsers(); setView("allUsers");  }}
                        user={selectedUser}
                        isAdmin={isAdmin}
                        checkIfAdmin={checkIfAdmin}
                        userDetails={userDetails}
                      />
                    )}
                    </Grid>
                    {
                    addUser && (
                      <PopupMenu>
                        <Typography variant="h6">Add User</Typography>
                          <Grid container spacing={2}>
                            <Grid size={12}>
                              <TextField
                                required
                                fullWidth
                                label="Email"
                                variant="outlined"
                                focused
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter email" 
                                sx={{ marginTop: '20px', backgroundColor: '#f5f5f5' }} 
                                error={userInvited && !email}
                              />
                            </Grid>
                            <Grid size={12}>
                              <FormControl fullWidth required error={userInvited && !userRole}>
                                <InputLabel id="role-label">Role</InputLabel>
                                <Select
                                  labelId="role-label"
                                  id="role"
                                  label="Role"
                                  value={userRole}
                                  onChange={(e) => setUserRole(e.target.value)}
                                  sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
                                >
                                  {roles.map((v, index) => (
                                    <MenuItem key={index} value={v}>
                                      {v}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid size={12}>
                              <FormControl fullWidth required error={userInvited && !teamName}>
                                <InputLabel id="organization-label">Organization</InputLabel>
                                <Select
                                  labelId="organization-label"
                                  id="organization"
                                  label="Organization"
                                  value={teamName}
                                  onChange={(e) => setTeamName(e.target.value)}
                                  sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
                                >
                                  {adminTeamNames.map((v, index) => (
                                    <MenuItem key={index} value={v}>
                                      {v}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid size={12}>
                              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                <ScribeButton variant="outlined" onClick={() => setAddUser(false)} sx={{ minWidth: 96, flexShrink: 0 }}>
                                  Close
                                </ScribeButton>
                                <ScribeButton variant="contained" onClick={() => {
                                    setUserInvited(true);
                                    if (!email || !userRole || !teamName) {
                                      setAlert('Please fill in all required fields.', 'error');
                                      return;
                                    }
                                    inviteUser();
                                  }} 
                                  sx={{ minWidth: 96, flexShrink: 0 }}>
                                  Confirm
                                </ScribeButton>
                              </Stack>
                            </Grid>
                          </Grid>
                      </PopupMenu> 
                      )
                    }
                    {
                      addCollaborators && (
                        <PopupMenu>
                          <Typography variant="h6">Add Collaborators</Typography>
                            <Grid container spacing={2}>
                              <Grid size={12} sx={{ marginTop: '20px' }}>
                                <FormControl fullWidth required error={formSubmitted && collaborators.length === 0}>
                                  <InputLabel id="collaborators-label">Collaborators</InputLabel>
                                  <Select
                                    labelId="collaborators-label"
                                    id="collaborators"
                                    label="Collaborators"
                                    multiple
                                    value={collaborators}
                                    onChange={(e) => setCollaborators(e.target.value)}
                                    MenuProps={{
                                      PaperProps: {
                                        style: {
                                          maxHeight: 250, // Smaller height since fewer options
                                          overflowY: 'auto',
                                        },
                                      },
                                      anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                      },
                                      transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                      },
                                    }}
                                    renderValue={(selected) => (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((ownerId) => {
                                          const member = teamMembersData.find(m => m.id === ownerId);
                                          return (
                                            <Chip
                                              key={ownerId}
                                              label={member ? member.name : 'Unknown'}
                                              size="small"
                                              onDelete={(e) => {
                                                e.stopPropagation();
                                                setCollaborators(prev => prev.filter(id => id !== ownerId));
                                              }}
                                              deleteIcon={<CloseIcon />}
                                              sx={{
                                                backgroundColor: '#29C0BB',
                                                color: 'white',
                                                '& .MuiChip-deleteIcon': {
                                                  color: 'white',
                                                  '&:hover': {
                                                    color: '#f5f5f5'
                                                  }
                                                }
                                              }}
                                            />
                                          );
                                        })}
                                      </Box>
                                    )}
                                  >
                                    {teamMembersData.map((member) => (
                                      <MenuItem 
                                        key={member.id} 
                                        value={member.id}
                                        sx={{
                                          backgroundColor: collaborators.includes(member.id) ? '#e3f2fd' : 'transparent'
                                        }}
                                      >
                                        <Checkbox 
                                          checked={collaborators.includes(member.id)}
                                          sx={{
                                            '&.Mui-checked': {
                                              color: '#29C0BB',
                                            }
                                          }}
                                        />
                                        {member.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {formSubmitted && collaborators.length === 0 && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 1 }}>
                                      Please select at least one collaborator
                                    </Typography>
                                  )}
                                </FormControl>
                              </Grid>
                              <Grid size={12}>
                                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>                   
                                  <Box onClick={() => { handleCloseCollaborators(); }} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer'}}>
                                    Close
                                  </Box>  
                                  <Box onClick={() => { 
                                      setFormSubmitted(true);
                                      if (collaborators.length === 0) {
                                        setAlert('Please select at least one collaborator.', 'error');
                                        return;
                                      }
                                      addCollaboratorsToTeam();
                                    }} 
                                    sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#ffffff", cursor: 'pointer'}}>
                                    Confirm
                                  </Box>  
                                </Stack>
                              </Grid>
                            </Grid>
                        </PopupMenu>
                      )
                    }
                  {
                    openDialog && (
                      <AlertDialog data={{open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog}}/>
                    )
                  }
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {(subscriptionActiveStatus !== false && userDetails && !userDetails.mode) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid #29C0BB',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#E0F7FA',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '36px',
                  color: '#29C0BB',
                }}
              >
                ⚙️
              </Typography>
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#333333',
                marginBottom: '16px',
              }}
            >
              Profile Setup Required
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                marginBottom: '32px',
                lineHeight: 1.6,
              }}
            >
              Your profile setup is incomplete. To continue using the application, please select your User Mode in Profile Settings.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#29C0BB',
                  color: '#FFFFFF',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#259B96',
                  },
                }}
                onClick={() => {
                  navigate('/settings');
                }}
              >
                Go to Settings
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
      {subscriptionActiveStatus === false && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid #FF6B6B',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#FFE6E6',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '36px',
                  color: '#FF6B6B',
                }}
              >
                ⚠️
              </Typography>
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#333333',
                marginBottom: '16px',
              }}
            >
              Subscription Inactive
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                marginBottom: '32px',
                lineHeight: 1.6,
              }}
            >
              Your subscription has expired and access to our platform is currently suspended. 
              To continue using our services and manage your patients, please reactivate your subscription from the settings.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#29C0BB',
                  color: '#FFFFFF',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#259B96',
                  },
                }}
                onClick={() => {
                  // Navigate to settings page - you might need to import useNavigate from react-router-dom
                  navigate('/settings');
                }}
              >
                Subscribe Now
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default UserList;
